import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { RefreshService } from '../services/refresh.service';
import { TokenService } from '../services/token.service';

const REFRESH_PATH = '/v2/authenticate/refresh';
// A 401 on switch must not be retried through refresh: the retry would replay
// the old seq and the API revokes the session on a stale sequence.
const SWITCH_PATH = '/v2/authenticate/switch';

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const refresher = inject(RefreshService);
  const tokens = inject(TokenService);
  const router = inject(Router);

  // Never recurse into ourselves on the refresh request.
  if (req.url.endsWith(REFRESH_PATH) || req.url.endsWith(SWITCH_PATH)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      return from(refresher.refresh()).pipe(
        catchError((refreshErr: unknown) => {
          // Refresh failed — RefreshService already cleared tokens. Send the
          // user to /login with a banner so they understand what happened.
          router.navigate(['/login'], { queryParams: { error: 'session-expired' } });
          return throwError(() => refreshErr);
        }),
        switchMap(() => {
          const fresh = tokens.getAccessToken();
          const retried = fresh
            ? req.clone({ setHeaders: { Authorization: `Bearer ${fresh}` } })
            : req;
          return next(retried);
        }),
      );
    }),
  );
};
